/**
 * PATH: apps/ai/src/pages/dashboard/IntelligenceSidebar.tsx
 * PURPOSE: Left panel — portfolio metrics, alerts, context-aware actions, email drafts
 */

import { useState, useEffect } from 'react';
import {
  Brain, Zap, BarChart3, Bell, Target, PanelLeftClose,
  Briefcase, AlertTriangle, TrendingUp, Wallet,
  Timer, Receipt, TrendingDown, DollarSign, PieChart,
  FileText, FileSearch, Calculator, Mail, Lightbulb,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  fetchPortfolioMetrics, fetchSmartAlerts, fetchReceivablesForecast,
  type PortfolioMetrics, type SmartAlert,
} from '../../services/aiStreaming';
import { checkOutlookConnection } from '../../services/outlookService';
import { formatCurrency, formatPercent } from './utils';
import { MetricCard } from './MetricCard';
import { AlertItem } from './AlertItem';
import { QuickActionButton } from './QuickActionButton';
import { EmailDraftModal } from './EmailDraftModal';
import type { Alert } from './types';

interface IntelligenceSidebarProps {
  onPromptClick: (prompt: string) => void;
  onCollapse: () => void;
}

export function IntelligenceSidebar({ onPromptClick, onCollapse }: IntelligenceSidebarProps) {
  const [metrics, setMetrics] = useState<PortfolioMetrics | null>(null);
  const [alerts, setAlerts] = useState<SmartAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [metricsData, alertsData, , outlookStatus] = await Promise.all([
          fetchPortfolioMetrics(), fetchSmartAlerts(), fetchReceivablesForecast(), checkOutlookConnection(),
        ]);
        if (metricsData) setMetrics(metricsData);
        if (alertsData) setAlerts(alertsData);
        setOutlookConnected(outlookStatus.connected);
      } catch (err) {
        console.error('Failed to load intelligence data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const dm = metrics || { activeJobs: 0, atRisk: 0, outstanding: 0, avgMargin: 0, contractValue: 0, totalInvoiced: 0, wip: 0, labourCost: 0, materialsCost: 0, totalCosts: 0, grossProfit: 0 };
  const marginTrend = [28, 32, 29, 35, 31, dm.avgMargin || 28.5];

  // --- Context-aware action items ---
  const actionItems = buildActionItems(dm);

  const displayAlerts: Alert[] = alerts.length > 0
    ? alerts.map(a => ({ type: a.type as Alert['type'], message: a.message, jobName: a.jobName }))
    : [{ type: 'warning' as const, message: 'Loading alerts...', jobName: undefined }];

  return (
    <div className="h-full flex flex-col overflow-hidden bg-card rounded-xl border border-border">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20">
                <Brain className="w-4.5 h-4.5 text-primary-foreground" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-background flex items-center justify-center border-2 border-primary">
                <Zap className="w-2 h-2 text-amber-500 fill-amber-500" />
              </span>
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">Intelligence Center</h2>
              <p className="text-[10px] text-muted-foreground">Portfolio Overview</p>
            </div>
          </div>
          <button onClick={onCollapse} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" title="Collapse panel">
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-5">
          {/* Key Metrics */}
          <section>
            <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 flex items-center gap-1.5"><BarChart3 className="w-3 h-3" /> Key Metrics</h3>
            <div className="grid grid-cols-2 gap-2">
              <MetricCard icon={Briefcase} label="Active Jobs" value={dm.activeJobs.toString()} status="neutral" loading={loading} onClick={() => onPromptClick("Give me an overview of all active jobs")} />
              <MetricCard icon={AlertTriangle} label="At Risk" value={dm.atRisk.toString()} status={dm.atRisk > 3 ? "critical" : dm.atRisk > 0 ? "warning" : "good"} loading={loading} onClick={() => onPromptClick("Which jobs need immediate attention?")} />
              <MetricCard icon={Wallet} label="WIP" value={formatCurrency(dm.wip || dm.outstanding)} status={(dm.wip || dm.outstanding) > 500000 ? "warning" : "neutral"} loading={loading} onClick={() => onPromptClick("What's our current Work in Progress (WIP)?")} />
              <MetricCard icon={TrendingUp} label="Avg Margin" value={formatPercent(dm.avgMargin)} sparkline={marginTrend} status={dm.avgMargin < 20 ? "warning" : dm.avgMargin < 25 ? "neutral" : "good"} loading={loading} onClick={() => onPromptClick("Rank all jobs by margin")} />
            </div>
          </section>

          {/* Alerts */}
          {!loading && alerts.length > 0 && (
            <section>
              <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                <Bell className="w-3 h-3" /> Alerts
                <span className="px-1.5 py-0.5 text-[9px] bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-full font-bold">{alerts.length}</span>
              </h3>
              <div className="space-y-2">
                {alerts.slice(0, 3).map((alert, i) => (
                  <AlertItem key={i} alert={{ type: alert.type as Alert['type'], message: alert.message, jobName: alert.jobName }} onClick={() => onPromptClick(`Tell me about: "${alert.message}"`)} />
                ))}
              </div>
            </section>
          )}

          {/* Recommended Actions */}
          <section>
            <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 flex items-center gap-1.5"><Target className="w-3 h-3" /> Recommended Actions</h3>
            <div className="space-y-2">
              {actionItems.map((item, idx) => (
                <QuickActionButton key={idx} icon={item.icon} title={item.title} description={item.description} color={item.color} onClick={() => onPromptClick(item.prompt)} />
              ))}
            </div>
          </section>

          {/* Quick Actions */}
          <section>
            <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 flex items-center gap-1.5"><Zap className="w-3 h-3" /> Quick Actions</h3>
            <div className="space-y-2">
              <QuickActionButton icon={FileText} title="Generate Report" description="Export summary PDF" color="teal" onClick={() => onPromptClick("Generate a comprehensive portfolio health report with recommendations")} />
              <QuickActionButton icon={Mail} title={outlookConnected ? "Create Email Draft" : "Draft Email"} description={outlookConnected ? "Opens in Outlook" : "Client communication"} color="blue"
                onClick={() => outlookConnected ? setShowEmailModal(true) : onPromptClick("Draft a professional email to follow up on outstanding invoices")} />
              <QuickActionButton icon={Calculator} title="Deep Analysis" description="Margin & cost trends" color="purple" onClick={() => onPromptClick("Perform a deep analysis of margin trends, cost overruns, and profitability across all active jobs")} />
              <QuickActionButton icon={FileSearch} title="Find Documents" description="Search files" color="amber" onClick={() => onPromptClick("Find all variation and change order documents from the last 30 days")} />
            </div>
          </section>

          {/* Pro Tip */}
          <section className="pt-3 border-t border-border">
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-primary/5 border border-primary/10">
              <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] font-medium text-primary">Pro Tip</p>
                <p className="text-[10px] text-muted-foreground leading-relaxed">Click any metric for instant AI analysis. Try "What should I focus on today?"</p>
              </div>
            </div>
          </section>
        </div>
      </div>

      {showEmailModal && <EmailDraftModal onClose={() => setShowEmailModal(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generate context-aware action items based on current metrics
// ---------------------------------------------------------------------------

type ActionItem = { icon: React.ComponentType<{ className?: string }>; title: string; description: string; color: any; prompt: string };

function buildActionItems(dm: { atRisk: number; labourCost: number; wip: number; outstanding: number; avgMargin: number; contractValue: number; totalInvoiced: number }): ActionItem[] {
  const items: ActionItem[] = [];

  if (dm.atRisk > 0) items.push({ icon: AlertTriangle, title: `Review ${dm.atRisk} At-Risk Jobs`, description: 'Jobs with negative WIP or over budget', color: 'red', prompt: `Analyze the ${dm.atRisk} at-risk jobs in detail. What are the key issues and recommended actions for each?` });
  if (dm.labourCost > 100000) items.push({ icon: Timer, title: 'Labour Cost Analysis', description: `${formatCurrency(dm.labourCost)} total labour`, color: 'teal', prompt: 'Analyze labour costs across all active jobs. Which jobs are over budget on hours? What are the productivity trends?' });
  if ((dm.wip || dm.outstanding) > 0) { const wipValue = dm.wip || dm.outstanding; items.push({ icon: Receipt, title: 'WIP Analysis', description: `${formatCurrency(wipValue)} work in progress`, color: 'amber', prompt: `We have ${formatCurrency(wipValue)} in WIP. Break down which jobs have the highest WIP and analyze the billing status.` }); }
  if (dm.avgMargin < 25 && dm.avgMargin > 0) items.push({ icon: TrendingDown, title: 'Margin Improvement', description: `Currently ${dm.avgMargin.toFixed(1)}% avg margin`, color: 'amber', prompt: 'Our average margin is below 25%. Which jobs are dragging down the average? What cost categories are causing issues?' });
  if (dm.contractValue > 0 && dm.totalInvoiced > 0) { const pct = (dm.totalInvoiced / dm.contractValue) * 100; if (pct < 50) items.push({ icon: DollarSign, title: 'Billing Opportunity', description: `Only ${pct.toFixed(0)}% of contract invoiced`, color: 'teal', prompt: 'Analyze the gap between contract value and invoiced amount. Which jobs have significant unbilled work?' }); }

  if (items.length < 3) {
    items.push({ icon: Target, title: 'Daily Priorities', description: 'What should I focus on today?', color: 'teal', prompt: 'Based on all portfolio data, what should I focus on today? Give me the top 5 priorities with specific actions.' });
    items.push({ icon: PieChart, title: 'Cost Center Breakdown', description: 'Labour, materials, subcontractors', color: 'purple', prompt: 'Give me a breakdown of costs across all active jobs: labour, materials, equipment, and subcontractor costs. Which cost centers are over budget?' });
    items.push({ icon: FileText, title: 'Quote vs Actual Review', description: 'Contract intake analysis', color: 'blue', prompt: 'Compare quoted amounts vs actual costs for active jobs. Which jobs have significant variances from their original quotes?' });
  }

  return items.slice(0, 4);
}
